import { takeLatest, put, select, all, delay, call } from 'redux-saga/effects';
import { cloneDeep, get } from 'lodash';
import moment from 'moment';
import { message } from 'antd';
import axios from 'axios';
import { dashboardsSelector } from '@store/tempDashboard/selectors';
import { applyFilter, updateInterval, toggleLayout } from '@store/common/actions';
import { resetDrillDown } from '@store/summaryContainer/drilldown/actions';
import { setMetrics, selectedMetricChart } from '@store/metricsChartContainer/actions';
import { pushDrilldownFilter } from '@store/dimensionContainer/actions';
import { METRICS_MAPPING, getFilterFromRules } from '@utils';
import { changeMode } from '@store/trends/actions';
import { setSelectedAiAlert, setAllExceptAnomalyPayload } from '@store/trends/aiAlerts/actions';
import { alertDiagnosticsSelector } from '@store/aiAlerts/diagnostics/selectors';
import { fetchAlertDiagnosticsApi } from '@store/aiAlerts/diagnostics/sagas';
import { GraphMetricMapping, AI_ALERT_DIAGNOSTIC_CHART_LABEL_MAP } from '@utils/aiAlerts/mapping';
import { setDimensions, setSharedState } from '@store/trends/base/actions';
import { LAYOUT_MAP, PRODUCT_MODE_MAP, isAppModule } from '@utils/constants';
import parseDimensions from '@store/trends/utils/parseDimensions';
import metricsStore from '@src/globalStore/metricsStore';
import { getLocalTime, formatSummaryObject } from '@store/aiAlerts/summary/helper';
import {
  aiAlertsMetricsMetaSelector,
  aiAlertsDimensionMappingSelector,
} from '@store/common/selectors';
import { querySelector } from '@store/metricsChartContainer/selectors';
import getMetricsData from '@api/metricContainer';
import { dimensionsListSelectorKeyValue } from '@store/common/dimensionList/selectors';
import { setSlice, setDefaultSlice } from '@store/trends/birdChart/actions';
import {
  DEFAULT_DIMENSIONS_FOR_AI_ALERTS_DIAGNOSTICS,
  ALERT_TYPE_CONTEXT_MAP,
  aiAlertMapping,
} from '@utils/aiAlerts/metrics';
import * as types from './types';
import { MODE_AI_ALERT_DIAGNOSTICS } from '../../constants';
import {
  allExceptAnamolyGraphOption,
  baselineGraphOption,
  thresholdGraphLineOption,
  brushLineSeverityData,
  severityBrushLineOption,
  tooltipPosition,
  t7GraphLineOption,
  projectedLineGraphOption,
  pcpGraphOption,
  pcpDropYAxis,
} from './lineGraphOptions';
import getFilterRuleForAiAlertRootCause from './rootCauseFilter';
import { DIMENSION_LIMIT } from '../../base/reducer';
import {
  getBrushLineDataBySeverity,
  getPcpDropForTooltip,
  getUniqueDevicesForTimeSeries,
  getUniqueDevicesSeries,
} from './utils';

const getFilterRuleFromRootCause = (
  rootCause,
  rootCauseDisplayLabel,
  dimensionName,
  dimensionKeys,
  aiAlertDimensionMapping,
) => {
  const rootCauseArray = rootCause.split('&&&');
  const rootCauseLabelArray = rootCauseDisplayLabel.split('&&&');
  const dimensionNameArray = dimensionName.split('&&&');
  let filterRuleObject = {};

  // if the rootcause city is not available in the portal db it will cause an error
  const cityError = {
    isCityCodeNotAvailable: false,
    cityName: '',
  };

  const isAllTraffic = rootCauseArray.length && rootCauseArray[0] === 'All Traffic';

  if (isAllTraffic) {
    return {
      filter: {
        name: 'All Traffic',
        rule: [],
      },
      cityError,
    };
  }

  rootCauseArray.forEach((rootCauseItem, index) => {
    const dimension = dimensionNameArray[index];
    const rootCauseLabel = rootCauseLabelArray[index];
    const mgwDimensionMapping = aiAlertDimensionMapping[dimension];

    const { field } = mgwDimensionMapping;
    let { key } = mgwDimensionMapping;

    key = key ? key : field;

    // this code is to notify the user about any missing city
    if (dimension === 'CITY' && !dimensionKeys[rootCauseItem].length) {
      cityError.isCityCodeNotAvailable = true;
      cityError.cityName = rootCauseItem;
    } else {
      const value =
        dimensionKeys[rootCauseItem] === 'Unknown'
          ? 'CONVIVA_INTERNAL_UNKNOWN'
          : dimensionKeys[rootCauseItem];

      const filterRuleForIndex = getFilterRuleForAiAlertRootCause(
        index,
        rootCauseItem,
        rootCauseLabel,
        field,
        value,
        key,
      );
      filterRuleObject = { ...filterRuleObject, ...filterRuleForIndex };
    }
  });

  const filter = getFilterFromRules(filterRuleObject);

  return {
    filter,
    cityError,
  };
};

function* getAllExceptAnamolyTimeSeriesData(metric, aiAlertFilter) {
  const currentMetric = GraphMetricMapping[metric] ? GraphMetricMapping[metric] : metric;

  const query = yield select(querySelector);
  const { reqBody } = cloneDeep(query);
  reqBody.queries[0].metrics = [currentMetric];

  // all traffic except the rootCause
  let allTrafficExceptAnamoly = aiAlertFilter.rule.map((filter) => {
    const updatedFilter = Object.assign({}, ...filter, { op: 'not_eq' }, { _order: 0 });
    return updatedFilter;
  });

  // all traffic execept all traffic is always 0
  if (aiAlertFilter.name === 'All Traffic') {
    allTrafficExceptAnamoly = [
      {
        field: 'All Traffic',
        op: 'eq',
        value: 'All Traffic',
        key: '',
        display: ' All Traffic',
        _order: 0,
      },
    ];
  }

  // if in App and All Traffic, we set filter to []
  reqBody.queries[0].filter =
    isAppModule && aiAlertFilter.name === 'All Traffic'
      ? // IF server will have a special logic handle OPPOSITE_ALL_TRAFFIC_FOR_AI_ALERT_DIAGNOSTIC
        [
          [
            {
              field: 'app_id',
              op: 'eq',
              value: 'OPPOSITE_ALL_TRAFFIC_FOR_AI_ALERT_DIAGNOSTIC',
              key: '',
              display: 'OPPOSITE_ALL_TRAFFIC_FOR_AI_ALERT_DIAGNOSTIC',
              _order: 0,
            },
          ],
        ]
      : [allTrafficExceptAnamoly];

  yield put(setAllExceptAnomalyPayload(reqBody));

  const source = axios.CancelToken.source();
  const resp = yield call(getMetricsData, reqBody, { cancelToken: source.token });
  const { data } = resp.data[0];
  const aiSeriesData = data.map((item) => [item.timestamp, item.results[currentMetric]]);

  return aiSeriesData;
}

function* getGraphData(selectedAiAlert, metric, aiAlertFilter, isAdAlert = false) {
  const { diagnosticsData, alertEvents, aiAlertKey, issueStartGraphTime } = selectedAiAlert;
  const diagnosticsAlertData = diagnosticsData && diagnosticsData.data;
  const thresholdData = [];
  const baselineData = [];
  // used in App AI Alert for ACTIVE_DEVICES metric
  const projectedData = [];
  const series = [];
  const tMinus7Data = [];
  const pcpDropData = [];
  const uniqueDevices = getUniqueDevicesForTimeSeries(diagnosticsAlertData);
  const pcpDrop = getPcpDropForTooltip(diagnosticsAlertData);
  const brushLineDataForSeverity = getBrushLineDataBySeverity(alertEvents);

  const aiTimeSeries = yield getAllExceptAnamolyTimeSeriesData(metric, aiAlertFilter);
  // other metric values should be shown in percentage. vst is shown as it is
  // const graphConverter = [
  //   'AD_AUTOALERTBITRATE',
  //   'AUTOALERTVST',
  //   'AUTOALERTPCP',
  //   'APP_PAGE_LOAD_TIME',
  //   'APP_SCREEN_LOAD_TIME',
  //   'APP_NETWORK_REQUEST_TIME',
  //   'APP_ACTIVE_DEVICES',
  // ].includes(aiAlertKey)
  //   ? 1
  //   : 100;
  const graphConverter = [
    'AUTOALERTVSF',
    'AUTOALERTEBVS',
    'AUTOALERTCIRR',
    'AUTOALERTVPF',
    'AD_AUTOALERTVSF',
    'AD_AUTOALERTBUFFERINGRATIO',
    'AD_AUTOALERTSLATEDURATIONRATIO',
  ].includes(aiAlertKey)
    ? 100
    : 1;

  diagnosticsAlertData.forEach((currentData) => {
    const threshold = [currentData.timestamp, currentData.results.threshold * graphConverter];
    const baseline = [currentData.timestamp, currentData.results.baseline * graphConverter];
    const tMinus7 = [currentData.timestamp, currentData.results.tMinus7 * graphConverter];
    const pcpDropD = [currentData.timestamp, currentData.results.pcpDrop * graphConverter];
    const projected = [currentData.timestamp, currentData.results.projected * graphConverter];
    thresholdData.push(threshold);
    baselineData.push(baseline);
    tMinus7Data.push(tMinus7);
    pcpDropData.push(pcpDropD);
    projectedData.push(projected);
  });
  // add issue start to the front
  // in case of overlapping events with same time, a second event like info should override start event
  brushLineDataForSeverity.unshift(brushLineSeverityData(issueStartGraphTime));
  if (!isAdAlert) {
    if (aiAlertKey !== 'AUTOALERTPCP' && aiAlertKey !== 'APP_ACTIVE_DEVICES') {
      series.push(allExceptAnamolyGraphOption(aiTimeSeries));
    }
    if (aiAlertKey !== 'AUTOALERTPCP' && aiAlertKey !== 'APP_ACTIVE_DEVICES') {
      series.push(thresholdGraphLineOption(thresholdData));
    }
    if (aiAlertKey === 'AUTOALERTPCP' || aiAlertKey === 'APP_ACTIVE_DEVICES') {
      series.push(t7GraphLineOption(tMinus7Data));
    }
  }

  if (aiAlertKey === 'AUTOALERTPCP' || aiAlertKey === 'APP_ACTIVE_DEVICES') {
    const projected = aiAlertKey === 'AUTOALERTPCP' ? baselineData : projectedData;
    series.push(projectedLineGraphOption(projected));
    series.push(
      pcpGraphOption(
        pcpDropData,
        AI_ALERT_DIAGNOSTIC_CHART_LABEL_MAP[window.conviva.module]?.PCP_OR_PCT,
      ),
    );
  } else {
    series.push(baselineGraphOption(baselineData));
  }

  return {
    tooltipPosition,
    uniqueDevices,
    pcpDrop,
    severityBrushLine: severityBrushLineOption(brushLineDataForSeverity),
    graphSeries: series,
    uniqueDevicesSeries: getUniqueDevicesSeries(diagnosticsAlertData),
  };
}

function* executeTasks(
  startTimestamp,
  endTimestamp,
  metricName,
  filter,
  selectedAiAlert,
  dimensions,
) {
  const { combinedMetricsKey } = metricsStore.getState();
  const { issueStartGraphTime, alertEvents } = selectedAiAlert;
  const sliceStartTime = new Date(issueStartGraphTime).getTime();
  const sliceEndTime = new Date(alertEvents[alertEvents.length - 1].graphTime).getTime();
  let secondaryMetric: string = metricName;
  if (metricName in METRICS_MAPPING) {
    secondaryMetric = METRICS_MAPPING[metricName];
  } else {
    if (combinedMetricsKey.app && combinedMetricsKey.app.includes(metricName)) {
      secondaryMetric = 'AppUniqueDevices';
    }
  }

  const metricList = Array.from(new Set([metricName, secondaryMetric]));

  const dimensionsList = yield select(dimensionsListSelectorKeyValue);
  const filteredDimension = parseDimensions(dimensions, dimensionsList);

  // adding an extra minute as MGW excludes last timestamp causing chart series alignment issue
  const updatedEndTimestamp = moment(endTimestamp).add(1, 'minute').format();

  const tasks = [
    put(changeMode(MODE_AI_ALERT_DIAGNOSTICS)),
    put(toggleLayout(LAYOUT_MAP.horizontal)),
    put(updateInterval(startTimestamp, updatedEndTimestamp, 'custom', 'ai-alerts')),
    put(setDefaultSlice([0, 100], [sliceStartTime, sliceEndTime])),
    put(setSlice([0, 100], [sliceStartTime, sliceEndTime])),
    put(resetDrillDown([])),
    put(pushDrilldownFilter(filter.name, filter.rule)),
    put(applyFilter(filter)),
    put(setSharedState({})), // resets base state
    put(selectedMetricChart(metricName)),
    put(setMetrics(metricList)),
    put(setDimensions(filteredDimension)),
  ];

  yield all(tasks);
  yield delay(300);
}

function setCityError(cityError) {
  if (cityError.isCityCodeNotAvailable) {
    message.error({
      key: 'cityError',
      content: `${cityError.cityName} city filter is not supported. Please contact Conviva support team`,
      duration: 10,
      onClick: () => message.destroy('cityError'),
    });
  }
}

const getDimensionsListForAlert = (
  metricName,
  dimensionName,
  dimensionMapping,
  aiAlertsMetricMeta,
  context,
): string[] => {
  const aiAlertMetric = aiAlertsMetricMeta.find((metric) => metric.metricKey === metricName);
  const dimensionNameArray = dimensionName.split('&&&');
  const filterDimensions = dimensionNameArray.map(
    (d) => dimensionMapping[d].key || dimensionMapping[d].field,
  );
  return [
    ...new Set([
      ...filterDimensions,
      ...(aiAlertMetric?.dimensionList || DEFAULT_DIMENSIONS_FOR_AI_ALERTS_DIAGNOSTICS[context]),
    ]),
  ].slice(0, DIMENSION_LIMIT.max);
};

function* initAiAlertsDiagnostics(dashboard) {
  try {
    const aiAlerts = get(dashboard, 'module:trends.aiAlerts');
    const aiAlertsMetricMeta = yield select(aiAlertsMetricsMetaSelector);
    const dimensionMapping = yield select(aiAlertsDimensionMappingSelector);
    let selectedAiAlert: any = {};
    const { selectedAlertId, selectedAlertType } = aiAlerts;
    const isAdAlert = selectedAlertType && selectedAlertType === 'AD_ALERT';
    const aiAlert = aiAlerts.selectedAiAlert ? aiAlerts.selectedAiAlert : null;
    const aiAlertsContext = ALERT_TYPE_CONTEXT_MAP[selectedAlertType] || PRODUCT_MODE_MAP.EI;
    if (!aiAlert) {
      const dimensionListObject = yield select(dimensionsListSelectorKeyValue);
      yield fetchAlertDiagnosticsApi({ alertType: selectedAlertType, alertId: selectedAlertId });
      const diagnosticsData = yield select(alertDiagnosticsSelector);
      const aiAlertFromDiagnosticDataResponse = formatSummaryObject({
        alert: diagnosticsData.summary,
        dimensionListObject,
        dimensionMapping,
        aiAlertsMetricMeta,
      });
      selectedAiAlert = { ...aiAlertFromDiagnosticDataResponse };
      selectedAiAlert.diagnosticsData = diagnosticsData;
      yield put(setSelectedAiAlert({ selectedAiAlert, selectedAlertId, selectedAlertType }));
      const { summary, dimensionKeys, data } = diagnosticsData;
      const { rootCause, dimensionName, rootCauseDisplayLabel } = summary;

      const { cityError, filter } = getFilterRuleFromRootCause(
        rootCause,
        rootCauseDisplayLabel,
        dimensionName,
        dimensionKeys,
        dimensionMapping,
      );

      // get the start and end timestamp for the timeseries from the ai alert diagnostics api
      const startTimestamp = data[0].timestamp;
      const endTimestamp = data[data.length - 1].timestamp;
      const startTime = getLocalTime(startTimestamp);
      const endTime = getLocalTime(endTimestamp);
      const aiAlertKey = selectedAiAlert.aiAlertKey;
      const metricMetaDetails = aiAlertsMetricMeta.find(
        (metric) => metric.metricKey === aiAlertKey,
      );
      const metricName = metricMetaDetails
        ? aiAlertsMetricMeta.metricKey
        : selectedAiAlert.pulse5MetricKey;

      const dimensions = getDimensionsListForAlert(
        metricName,
        dimensionName,
        dimensionMapping,
        aiAlertsMetricMeta,
        aiAlertsContext,
      );

      yield executeTasks(
        startTimestamp,
        endTimestamp,
        metricName,
        filter,
        selectedAiAlert,
        dimensions,
      );

      const graphSeriesData = yield getGraphData(selectedAiAlert, metricName, filter, isAdAlert);
      const { severityBrushLine, graphSeries, uniqueDevices, pcpDrop, uniqueDevicesSeries } =
        graphSeriesData;
      // set the derived details in then select
      selectedAiAlert.startTime = startTime;
      selectedAiAlert.endTime = endTime;
      selectedAiAlert.startTimestamp = startTimestamp;
      selectedAiAlert.endTimestamp = endTimestamp;
      selectedAiAlert.filter = filter;
      selectedAiAlert.graphSeries = graphSeries;
      selectedAiAlert.severityBrushLine = severityBrushLine;
      selectedAiAlert.uniqueDevices = uniqueDevices;
      selectedAiAlert.uniqueDevicesSeries = uniqueDevicesSeries;
      selectedAiAlert.pcpDrop = pcpDrop;
      selectedAiAlert.pcpDropYAxis = pcpDropYAxis;
      selectedAiAlert.cityError = cityError;

      yield put(setSelectedAiAlert({ selectedAiAlert, selectedAlertId, selectedAlertType }));
      setCityError(cityError);

      yield put({ type: types.END_FETCHING });
    } else {
      const { startTimestamp, endTimestamp, filter, cityError, pulse5MetricKey, diagnosticsData } =
        aiAlert;
      const metricName = pulse5MetricKey;

      const dimensions = getDimensionsListForAlert(
        metricName,
        diagnosticsData.summary.dimensionName,
        dimensionMapping,
        aiAlertsMetricMeta,
        aiAlertsContext,
      );

      yield executeTasks(
        startTimestamp,
        endTimestamp,
        metricName,
        filter,
        selectedAiAlert,
        dimensions,
      );
      setCityError(cityError);
      yield put({ type: types.END_FETCHING });
    }
  } catch (error) {
    console.error(error);
    throw error;
  }
}

function* initTempDashboard({ tempDashboardId }) {
  const dashboards = yield select(dashboardsSelector);
  const tempDashboard = dashboards[tempDashboardId];

  if (!tempDashboard || !tempDashboard.dashboard) {
    return;
  }

  yield put({ type: types.START_FETCHING });
  const { dashboard } = tempDashboard;
  yield initAiAlertsDiagnostics(dashboard);
}

function* watchInitTempDashboard() {
  yield takeLatest([types.INIT_TEMP_DASHBOARD], initTempDashboard);
}

export default [watchInitTempDashboard];
